import * as d3 from 'd3';

import { formatNumber } from '../../utils/intl';
import events from './turbomap-events';
import settings from './turbomap-settings';
import { getDirectionOfCollection } from './turbomap-utils';

const GROUP_MARGIN = 5;
const SQUARE_SIZE_MARGIN = 0.95;

class TurbomapNodeFactory {
	constructor(elementsCount, areaWidth, areaHeight) {
		this.elementsCount = elementsCount;
		this.areaHeight = areaHeight;
		this.areaWidth = areaWidth;

		this.productNodeSize = this.getProductElementSize();
		this.elementsPerRow = Math.floor(areaWidth / this.productNodeSize);
		this.elementsPerColumn = Math.floor(areaHeight / this.productNodeSize);
		this.maxItemsPerArea = this.elementsPerRow * this.elementsPerColumn;
		this.maxElementSizePerArea = Math.min(this.areaWidth, this.areaHeight) - GROUP_MARGIN;

		this.groupElementScale = d3.scaleSqrt().domain([this.maxItemsPerArea + 1, elementsCount])
			.range([this.productNodeSize + 10, this.maxElementSizePerArea]);
	}

	// Used for determining the start on x-axis for whole rows and rows with less than the max product nodes on it.
	// Less product nodes are positioned on a row in the center if odd, otherwise just a halfsize extra
	calculateHorizontalStartProduct(products, elementIndex) {
		const currentColumn = Math.floor(elementIndex / this.elementsPerRow);
		const cumulativeRowLimit = (currentColumn + 1) * this.elementsPerRow;
		const halfsize = this.productNodeSize * 0.5;

		if (products.length > cumulativeRowLimit) {
			return halfsize;
		}

		const itemsOnRow = products.length - (currentColumn * this.elementsPerRow);
		const startSpace = Math.floor((this.elementsPerRow - itemsOnRow) * 0.5);

		if (this.elementsPerRow % 2 === itemsOnRow % 2) {
			return halfsize + (startSpace * this.productNodeSize);
		}

		return this.productNodeSize + (startSpace * this.productNodeSize);
	}

	// Used for determining the start on y-axis for whole rows and rows with less nodes on it
	calculateVerticalStartProduct(products) {
		const maxRows = Math.floor(products.length / (this.elementsPerRow + 1));

		const bucketCenter = this.areaHeight * 0.5;

		if (maxRows === 0) {
			return bucketCenter;
		}

		return (this.areaHeight - (maxRows * this.productNodeSize)) * 0.5;
	}

	// used to calculate the product node x and y position. An svg translation is used
	calculateTranslationProduct(products, elementIndex) {
		const xStart = this.calculateHorizontalStartProduct(products, elementIndex);
		const yStart = this.calculateVerticalStartProduct(products, elementIndex);

		const x = xStart + (this.productNodeSize * (elementIndex % this.elementsPerRow));
		const y = yStart + (this.productNodeSize * Math.floor(elementIndex / this.elementsPerRow));


		return `translate(${x}, ${y})`;
	}

	// calculates the group translation (x,y) of the node
	calculateTranslationGroup() {
		const x = this.areaWidth * 0.5;
		const y = this.areaHeight * 0.5;

		return `translate(${x}, ${y})`;
	}

	// calculates how large a square or circle can be, to fill an area up with `elementsCount`
	getProductElementSize() {
		const area = this.areaWidth * this.areaHeight;
		const squareAreaMax = area / this.elementsCount;
		const squareSizeMax = Math.sqrt(squareAreaMax) * SQUARE_SIZE_MARGIN;

		const nodeWidth = Math.ceil(this.areaWidth / squareSizeMax);
		const nodeHeight = Math.ceil(this.areaHeight / squareSizeMax);

		const radius = Math.min(this.areaWidth / nodeWidth, this.areaHeight / nodeHeight);

		return Math.max(settings.minimalProductDiameter, radius);
	}

	// creates either a product node or a group node based on the amount of products in an area
	createNode(products, mapKey) {
		if (products.length > this.maxItemsPerArea) {
			return this.createGroupNode(products, mapKey);
		}

		return this.createProductNode(products);
	}

	// Adds extra properties to a product node
	createProductNode(products) {
		return products.map((p, elementIndex) => {
			p.elementSize = this.productNodeSize;
			p.id = p.isin;
			p.clickEvent = events.PRODUCT_CLICKED;
			p.label = formatNumber(p.barrier);
			p.translation = this.calculateTranslationProduct(products, elementIndex);

			return p;
		});
	}

	// creates a single element array of one node; the group
	createGroupNode(products, mapKey) {
		return [{
			subProducts: products,
			id: mapKey,
			direction: getDirectionOfCollection(products),
			elementSize: this.groupElementScale(products.length),
			clickEvent: events.GROUP_CLICKED,
			label: products.length,
			translation: this.calculateTranslationGroup(),
		}];
	}
}

export default TurbomapNodeFactory;
